"use client"

// components/tracker/digest-next/use-sheet-band.ts
//
// Which wheel the card mounts (FE-DIGEST-1 Phase D4c2): "wide" from a 928px
// `digest-next-sheet`, "tablet" from 560 to 927, "mobile" below 560, the same
// lines as the module's container queries (`max-width: 927.98px` and
// `559.98px`). The narrow block needs tablet against mobile only for the
// order of its footer, which differs in Paper (D4c2 round 2). Read by a ResizeObserver on the
// sheet element itself, found from the card's own wrapper.
//
// WHY A WIDTH CHECK IN SCRIPT. Each wheel owns a live WebGL gradient, and the
// rule is one renderer per card, so the two cannot both be mounted and one
// hidden by CSS. The wheel only appears after the client has read the
// edition, so there is no server-rendered wheel to flash. Until the first
// measure the band is null and the card renders neither wheel. The measure
// runs in a layout effect, so that empty frame is never painted.

import { useLayoutEffect, useState, type RefObject } from "react"

import styles from "@/components/tracker/digest-next/digest-next.module.css"

export type SheetBand = "wide" | "tablet" | "mobile"

/** The module's lines: 927.98px or less is narrow, 559.98px or less mobile. */
const NARROW_MAX = 927.98
const MOBILE_MAX = 559.98

function bandOf(width: number): SheetBand {
  return width > NARROW_MAX ? "wide" : width > MOBILE_MAX ? "tablet" : "mobile"
}

export function useSheetBand(ref: RefObject<HTMLElement | null>): SheetBand | null {
  const [band, setBand] = useState<SheetBand | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // The sheet is the container the CSS measures. Outside it (never on this
    // page) the card's own wrapper stands in.
    const sheet = el.closest<HTMLElement>(`.${styles.dnSheet}`) ?? el
    // The content box, as a container query reads it.
    const read = () => {
      const cs = getComputedStyle(sheet)
      const pad = Number.parseFloat(cs.paddingLeft) + Number.parseFloat(cs.paddingRight)
      setBand(bandOf(sheet.getBoundingClientRect().width - (Number.isFinite(pad) ? pad : 0)))
    }
    read()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (entry) setBand(bandOf(entry.contentRect.width))
    })
    observer.observe(sheet)
    return () => observer.disconnect()
  }, [ref])
  return band
}
